import { CheckRing } from "@/components/optilase/icons";
import { CtaButton } from "@/components/optilase/cta-button";
import { type ButtonVariant } from "@/components/ui/button";
import { TextLink } from "@/components/optilase/text-link";
import { headingOn, type Surface } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";

export type GuideTile = { question: string; answerLabel: string; href: string };

export type GuideViewProps = {
  heading: string;
  intro?: string;
  tiles: GuideTile[];
  panel?: {
    heading: string;
    body?: string;
    points?: string[];
    ctaLabel: string;
    ctaVariant?: ButtonVariant;
    ctaHref?: string;
    ctaAction?: string | null;
  };
  surface?: Surface | null;
};

export default function GuideView({ heading, intro, tiles, panel, surface }: GuideViewProps) {
  return (
    <section
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 md:py-12 lg:px-20 lg:py-20", surface ? "" : "bg-navy-03")}
    >
      <div className="mx-auto max-w-7xl">
        <h2 className={cn("text-section", headingOn(surface, "text-teal-05"))}>{heading}</h2>
        {intro ? (
          <p className="mt-5 max-w-7xl text-base leading-relaxed tracking-[-0.01em] text-white lg:text-lg">
            {intro}
          </p>
        ) : null}

        <ul className="mt-8 grid gap-4 md:grid-cols-4 md:gap-3 lg:mt-10 lg:gap-6">
          {tiles.map((tile) => (
            <li
              key={tile.question}
              className="flex flex-col justify-between gap-4 rounded-xl border border-white/20 p-6 md:p-4 lg:p-6"
            >
              <p className="text-base leading-snug tracking-[-0.01em] text-white lg:text-lg">
                {tile.question}
              </p>
              <TextLink
                href={tile.href}
                className="text-teal-05 whitespace-normal"
                arrowClassName="mt-[0.4em] self-start"
              >
                {tile.answerLabel}
              </TextLink>
            </li>
          ))}
        </ul>

        {panel ? (
          <div className="bg-navy-02 mt-8 grid gap-6 rounded-2xl px-6 py-8 lg:mt-12 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-10 lg:px-10 lg:py-10">
            <div className="flex flex-col gap-4">
              <h3 className="text-h3 font-bold text-white">{panel.heading}</h3>
              {panel.body ? (
                <p className="text-teal-05 max-w-160 text-base leading-relaxed tracking-[-0.01em] lg:text-lg">
                  {panel.body}
                </p>
              ) : null}
              {panel.points?.length ? (
                <ul className="flex flex-col gap-2 md:flex-row md:flex-wrap md:gap-x-8 md:gap-y-2">
                  {panel.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-white">
                      <CheckRing className="mt-[3px] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <CtaButton
              href={panel.ctaHref}
              action={panel.ctaAction}
              variant={panel.ctaVariant ?? "orange"}
              keepOnMobile
              className="w-full lg:w-fit"
            >
              {panel.ctaLabel}
            </CtaButton>
          </div>
        ) : null}
      </div>
    </section>
  );
}
